import { useEffect, useMemo, useState } from "react";
import { RefreshCw, ExternalLink, ArrowUp, ArrowDown, Minus, HelpCircle } from "lucide-react";
import api from "@/lib/api";

const ACTION_STYLES = {
  raise: "text-amber-300 border-amber-400/40 bg-amber-400/[0.06]",
  lower: "text-emerald-300 border-emerald-400/40 bg-emerald-400/[0.06]",
  no_change: "text-[#8E9AA6] border-white/10 bg-white/[0.02]",
  unmatched: "text-[#8E9AA6] border-white/10 bg-white/[0.02]",
};

const ACTION_ICON = {
  raise: ArrowUp,
  lower: ArrowDown,
  no_change: Minus,
  unmatched: HelpCircle,
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function PricingReports() {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/pricing/competitor-reports?limit=12");
      setList(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load reports");
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    if (!id) return;
    setError(null);
    try {
      const { data } = await api.get(`/admin/pricing/competitor-reports/${id}`);
      setDetail(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to load report detail");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const triggerScrape = async () => {
    setScraping(true);
    setError(null);
    try {
      const { data } = await api.post("/admin/pricing/competitor-scrape");
      if (data?.status === "rate_limited") {
        setError(data.message || "Rate-limited — try again shortly.");
      }
      await refresh();
      if (data?.report_id) setSelectedId(data.report_id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Scrape failed");
    } finally {
      setScraping(false);
    }
  };

  const diffs = useMemo(() => detail?.report?.diffs || [], [detail]);
  const summary = detail?.report?.summary;

  const grouped = useMemo(() => {
    const g = {};
    for (const d of diffs) {
      g[d.group] = g[d.group] || [];
      g[d.group].push(d);
    }
    return g;
  }, [diffs]);

  return (
    <div data-testid="admin-pricing-reports" className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#C5A24A] mb-2">
            Advisory · Read-Only
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-white">
            Competitor Pricing Reports
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-[#8E9AA6] font-light leading-relaxed">
            Monthly diff of Phantasm marketing prices against
            {" "}
            <a
              className="text-white hover:text-[#C5A24A] transition-colors"
              href="https://bostonluxurychauffeur.com/boston-limo-service-cost/"
              target="_blank"
              rel="noopener noreferrer"
            >
              bostonluxurychauffeur.com <ExternalLink size={11} className="inline" />
            </a>
            . Reports auto-generate at most once per 30 days when the admin dashboard is
            accessed. No prices change automatically — this is advisory only.
          </p>
        </div>
        <button
          type="button"
          onClick={triggerScrape}
          disabled={scraping}
          data-testid="admin-trigger-scrape"
          className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white hover:text-black disabled:opacity-50 px-5 py-3 text-[10.5px] uppercase tracking-[0.28em] transition-colors"
        >
          <RefreshCw size={13} strokeWidth={1.5} className={scraping ? "animate-spin" : ""} />
          {scraping ? "Scraping…" : "Trigger Fresh Scrape"}
        </button>
      </header>

      {error && (
        <div
          data-testid="admin-pricing-error"
          className="border border-amber-400/40 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-200"
        >
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {!loading && list.length === 0 && (
        <div
          data-testid="admin-pricing-empty"
          className="border border-white/10 bg-[#121214]/70 px-5 py-6 text-sm text-[#8E9AA6] font-light"
        >
          No competitor pricing reports yet. Reports generate automatically once every 30 days
          when the admin dashboard is accessed. You can also trigger a fresh scrape manually.
        </div>
      )}

      {list.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Report list */}
          <aside className="border border-white/10 bg-[#121214]/70">
            <div className="px-4 py-3 text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] border-b border-white/[0.06]">
              Recent Reports
            </div>
            <ul data-testid="admin-report-list" className="divide-y divide-white/[0.06]">
              {list.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    data-testid={`admin-report-${r.id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors ${
                      selectedId === r.id ? "bg-white/[0.04]" : ""
                    }`}
                  >
                    <div className="text-xs text-white leading-snug">{fmtDate(r.generated_at)}</div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#8E9AA6] mt-1">
                      {r.summary?.rows_needing_update ?? 0} needing update ·{" "}
                      {r.summary?.rows_matched ?? 0}/{r.summary?.rows_total ?? 0} matched
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Detail */}
          <section data-testid="admin-report-detail" className="border border-white/10 bg-[#121214]/70">
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] mb-1">
                  Report Detail
                </div>
                <div className="text-sm text-white">
                  {fmtDate(detail?.generated_at)}
                </div>
              </div>
              {summary && (
                <div className="hidden md:flex gap-4 text-[10px] uppercase tracking-[0.24em]">
                  <Stat label="OK" value={summary.rows_no_change} color="text-[#8E9AA6]" />
                  <Stat label="Raise" value={summary.rows_raise} color="text-amber-300" />
                  <Stat label="Lower" value={summary.rows_lower} color="text-emerald-300" />
                  <Stat label="Unmatched" value={summary.rows_unmatched} color="text-[#8E9AA6]" />
                </div>
              )}
            </div>

            {detail?.report?.competitor_status && detail.report.competitor_status !== "success" && (
              <div className="mx-5 mt-4 border border-amber-400/40 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-200">
                Competitor scrape status: <b>{detail.report.competitor_status}</b>
                {detail.report.competitor_error_message ? ` — ${detail.report.competitor_error_message}` : ""}
                . Diff generated against Phantasm marketing prices only.
              </div>
            )}

            <div className="p-5 space-y-6">
              {Object.entries(grouped).map(([group, rows]) => (
                <div key={group}>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#C5A24A] mb-3">
                    {group}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b border-white/[0.06]">
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2">Row</th>
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2 text-right">Phantasm</th>
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2 text-right">Competitor</th>
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2 text-right">Target ×0.95</th>
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2 text-right">Δ</th>
                          <th className="text-[10px] uppercase tracking-[0.24em] text-[#8E9AA6] px-3 py-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((d, i) => (
                          <DiffRow key={`${d.label}-${i}`} d={d} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center">
      <div className={`font-serif text-2xl tabular-nums ${color}`}>{value ?? 0}</div>
      <div className="text-[#8E9AA6] mt-0.5">{label}</div>
    </div>
  );
}

function DiffRow({ d }) {
  const Icon = ACTION_ICON[d.action] || Minus;
  const cls = ACTION_STYLES[d.action] || ACTION_STYLES.no_change;
  const pRange = d.phantasm_low === d.phantasm_high ? `$${d.phantasm_low}` : `$${d.phantasm_low}–$${d.phantasm_high}`;
  const cRange =
    d.competitor_low != null
      ? d.competitor_low === d.competitor_high
        ? `$${d.competitor_low}`
        : `$${d.competitor_low}–$${d.competitor_high}`
      : "—";
  const tRange =
    d.target_phantasm_low != null
      ? d.target_phantasm_low === d.target_phantasm_high
        ? `$${d.target_phantasm_low}`
        : `$${d.target_phantasm_low}–$${d.target_phantasm_high}`
      : "—";
  const deltaText =
    d.delta_low == null
      ? "—"
      : d.delta_low === d.delta_high
      ? fmtDelta(d.delta_low)
      : `${fmtDelta(d.delta_low)} / ${fmtDelta(d.delta_high)}`;
  return (
    <tr className="border-b border-white/[0.04] last:border-b-0">
      <td className="px-3 py-3 text-white text-[13px] leading-snug">{d.label}</td>
      <td className="px-3 py-3 text-right text-[#E6E6E6] tabular-nums">{pRange}</td>
      <td className="px-3 py-3 text-right text-[#8E9AA6] tabular-nums">{cRange}</td>
      <td className="px-3 py-3 text-right text-white tabular-nums">{tRange}</td>
      <td className="px-3 py-3 text-right text-[#E6E6E6] tabular-nums">{deltaText}</td>
      <td className="px-3 py-3 text-center">
        <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] ${cls}`}>
          <Icon size={11} strokeWidth={1.5} />
          {d.action.replace("_", " ")}
        </span>
      </td>
    </tr>
  );
}

function fmtDelta(n) {
  if (n === 0) return "$0";
  return n > 0 ? `+$${n}` : `−$${Math.abs(n)}`;
}
